import React, { createContext, useContext } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// SSR initial-data plumbing.
//
// During SSR, the entry-server renders each dynamic public route with a
// route-specific loader (see ./loaders.ts). The loader's output is serialised
// into the HTML as `window.__VELLUM_INITIAL_DATA__` and passed into the React
// tree via this context.
//
// On the client, entry-client reads the same window global on boot and feeds
// it in the same way. Pages that support SSR should use `useInitialSSRData<T>`
// to read their loader's payload and skip their first API call when present.
//
// Why a context and not just read window directly from each page:
//
//   - Server renders have no `window`. A context lets the same hook work in
//     both environments without needing typeof-window guards everywhere.
//   - After first render, the data has been consumed and hydration is done —
//     we null it out so stale SSR data doesn't leak into later client-side
//     navigations to a different route with the same page component.
// ─────────────────────────────────────────────────────────────────────────────

export interface SSRInitialData {
  /** Route pattern that produced this data, for debugging + sanity checks. */
  route: string;
  /** The loader's payload — shape is per-route, see ./loaders.ts. */
  payload: unknown;
}

interface SSRContextValue {
  /** Present on the first render after SSR/hydration; null afterwards. */
  initialData: SSRInitialData | null;
  /** True during the server render. Useful for guarding browser-only side effects. */
  isServer: boolean;
}

const SSRContext = createContext<SSRContextValue>({
  initialData: null,
  isServer: false,
});

export function SSRProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: SSRContextValue;
}): JSX.Element {
  return <SSRContext.Provider value={value}>{children}</SSRContext.Provider>;
}

/**
 * Access SSR-prefetched data for the current route. Returns null if no SSR
 * data was prefetched (the common case for client-side navigation or pages
 * that aren't in the SSR route list).
 *
 * Pages using this must:
 *   1. Check if the payload shape matches what they expect (cheap type guard).
 *   2. Use it as the initial state for their data-fetching hook, skipping the
 *      first fetch on mount when data is present.
 *   3. Still set up a refresh path — SSR data is up to 1 min old (see the
 *      Cache-Control header we set on SSR responses) and may need refreshing
 *      for logged-in users who see different content (premium tiers, etc.).
 *
 * Usage pattern in a page component:
 *
 *   const ssr = useInitialSSRData<JournalData>((d) =>
 *     d?.route === '/journals/:username/:slug' ? (d.payload as JournalData) : null,
 *   );
 *   const [journal, setJournal] = useState<JournalData | null>(ssr);
 *   useEffect(() => {
 *     if (!ssr) fetchJournal(username, slug).then(setJournal);
 *   }, [username, slug]);
 */
export function useInitialSSRData<T>(extract: (data: SSRInitialData | null) => T | null): T | null {
  const ctx = useContext(SSRContext);
  return extract(ctx.initialData);
}

/** True when executing on the server. Cheaper than typeof-window checks. */
export function useIsServer(): boolean {
  return useContext(SSRContext).isServer;
}
